import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  // Button,
  Flex,
  Heading,
  Item,
  Key,
  Picker,
  Text,
} from "@adobe/react-spectrum";
import { MediaDevice } from "../../types/webrtc.type";
import { useEffect, useState } from "react";
import Emitter from "../../emitter";
import { 
  API_UPDATE_RING_MEDIA_DEVICE,
  API_UPDATE_SPEAKER_DEVICE,
  API_UPDATE_MICROPHONE_DEVICE,
} from "../../constants/events";
import storeRTK from "../../store/storeRTK";
import { 
  updateSpeakerDeviceAction,
  updateMicrophoneDeviceAction,
} from "../../actions/webrtc";
import { createRingDeviceNotification } from "../../utils/utils";

// Import test audio file
// import dangRing from "../../utils/audio/dang.mp3";

type MappedMediaDevice = {
  id: string;
  name: string;
  displayName: string;
  type: string;
};

const AudioSettings = () => {
  const webrtc = useSelector((state: RootState) => state.webrtc);
  const { 
    ringtoneOutoutDeviceId, 
    speakerDeviceId, 
    microphoneDeviceId,
    mediaDevices
  } = webrtc;
  
  // State for each device type
  const [selectedRingerDeviceId, setSelectedRingerDeviceId] = useState<string>("");
  const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = useState<string>("");
  const [selectedMicDeviceId, setSelectedMicDeviceId] = useState<string>("");
  
  // Separate device lists
  const [outputDevices, setOutputDevices] = useState<MappedMediaDevice[]>([]);
  const [inputDevices, setInputDevices] = useState<MappedMediaDevice[]>([]);
  
  // Audio refs
  // const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const audioRef = useRef<HTMLAudioElement | null>(null);

  // Map and filter media devices when they change
  useEffect(() => {
    // Filter audio output devices (speaker/ringer)
    const mappedOutputDevices =
      mediaDevices
        ?.map((device: MediaDevice) => {
          return {
            id: device.deviceId,
            name: device.label,
            displayName: device.label,
            type: device.kind,
          };
        })
        .filter(
          (device: MappedMediaDevice) =>
            device.name &&
            device.id &&
            device.type === "audiooutput" &&
            !device?.name.toLowerCase().includes("virtual")
        ) || [];
    
    // Filter audio input devices (microphone)
    const mappedInputDevices =
      mediaDevices
        ?.map((device: MediaDevice) => {
          return {
            id: device.deviceId,
            name: device.label,
            displayName: device.label,
            type: device.kind,
          };
        })
        .filter(
          (device: MappedMediaDevice) =>
            device.name &&
            device.id &&
            device.type === "audioinput" &&
            !device?.name.toLowerCase().includes("virtual")
        ) || [];
    
    setOutputDevices(mappedOutputDevices);
    setInputDevices(mappedInputDevices);
    
    // Default device selection is now handled centrally in webrtc.ts controller
    // The useEffects below will sync local state from Redux store values
  }, [mediaDevices]);


  // Sync speaker device from Redux
  useEffect(() => {
    if (speakerDeviceId && outputDevices.length > 0) {
      const deviceExists = outputDevices.some(
        (device: MappedMediaDevice) => device.id === speakerDeviceId
      );
      if (deviceExists) {
        setSelectedSpeakerDeviceId(speakerDeviceId);
      }
    }
  }, [speakerDeviceId, outputDevices]);

  // Sync microphone device from Redux
  useEffect(() => {
    if (microphoneDeviceId && inputDevices.length > 0) {
      const deviceExists = inputDevices.some(
        (device: MappedMediaDevice) => device.id === microphoneDeviceId
      );
      if (deviceExists) {
        setSelectedMicDeviceId(microphoneDeviceId);
      }
    }
  }, [microphoneDeviceId, inputDevices]);

  // Sync ringer device from Redux
  useEffect(() => {
    if (ringtoneOutoutDeviceId && outputDevices.length > 0) {
      const deviceExists = outputDevices.some(
        (device: MappedMediaDevice) => device.id === ringtoneOutoutDeviceId
      );
      if (deviceExists) {
        setSelectedRingerDeviceId(ringtoneOutoutDeviceId);
      }
    }
  }, [ringtoneOutoutDeviceId, outputDevices]);

  // Handlers for device selection
  const handleSelectSpeaker = (deviceId: string) => {
    setSelectedSpeakerDeviceId(deviceId);
    storeRTK.dispatch(updateSpeakerDeviceAction(deviceId));
    Emitter.emit(API_UPDATE_SPEAKER_DEVICE, {
      body: { deviceId },
    });
  };

  const handleSelectMicrophone = (deviceId: string) => {
    setSelectedMicDeviceId(deviceId);
    storeRTK.dispatch(updateMicrophoneDeviceAction(deviceId));
    Emitter.emit(API_UPDATE_MICROPHONE_DEVICE, {
      body: { deviceId },
    });
  };

  const handleSelectRinger = (deviceId: string) => {
    const selectedDeviceInfo = outputDevices.find(
      (device: MappedMediaDevice) => device.id === deviceId
    ) as MappedMediaDevice | undefined;
    
    setSelectedRingerDeviceId(deviceId);
    Emitter.emit(API_UPDATE_RING_MEDIA_DEVICE, {
      body: { deviceId },
    });

    if (selectedDeviceInfo) {
      createRingDeviceNotification(selectedDeviceInfo.displayName);
    }
  };

  return (
    <Flex direction={"column"} marginX={"1.875%"}>
      <Heading level={2}>Audio Devices</Heading>
      
      {/* Speaker Picker */}
      <Flex direction="row" gap="size-100" alignItems="end" marginTop="size-100">
        <Picker
          label="Speaker"
          onSelectionChange={(key: Key | null) =>
            handleSelectSpeaker(key as string)
          }
          selectedKey={selectedSpeakerDeviceId}
          defaultSelectedKey={selectedSpeakerDeviceId}
          width={"20.26%"}
          marginTop={"size-200"}
        >
          {outputDevices.map((device: MappedMediaDevice) => (
            <Item key={device.id} textValue={device.name}>
              {device.displayName}
            </Item>
          ))}
        </Picker>
        {/* <Button
            variant="cta"
            UNSAFE_style={{
              height: "28px",
              fontSize: "12px",
              padding: "0 12px",
            }}
            onPress={testSpeakerHandler}
          >
            Test Speaker
          </Button> */}
      </Flex>
      <Flex direction="row" gap="size-100" alignItems="center" marginTop="size-100">
        <Text
          UNSAFE_style={{
            fontSize: "12px",
            color: "#6E6E6E",
            fontStyle: "italic",
          }}
        >
          Audio device for call conversations
        </Text>
      </Flex>

      {/* Microphone Picker */}
      <Picker
        label="Microphone"
        onSelectionChange={(key: Key | null) =>
          handleSelectMicrophone(key as string)
        }
        selectedKey={selectedMicDeviceId}
        defaultSelectedKey={selectedMicDeviceId}
        width={"20.26%"}
        marginTop={"size-400"}
      >
        {inputDevices.map((device: MappedMediaDevice) => (
          <Item key={device.id} textValue={device.name}>
            {device.displayName}
          </Item>
        ))}
      </Picker>
      <Text
        UNSAFE_style={{
          fontSize: "12px",
          color: "#6E6E6E",
          marginTop: "8px",
          marginBottom: "16px",
          fontStyle: "italic",
        }}
      >
        Audio input device for call conversations
      </Text>

      {/* Ringer Picker */}
      <Flex direction="row" gap="size-100" alignItems="end" marginTop="size-100">
      <Picker
        label="Ringer"
        onSelectionChange={(key: Key | null) =>
          handleSelectRinger(key as string)
        }
        selectedKey={selectedRingerDeviceId}
        defaultSelectedKey={selectedRingerDeviceId}
        width={"20.26%"}
        marginTop={"size-200"}
      >
        {outputDevices.map((device: MappedMediaDevice) => (
          <Item key={device.id} textValue={device.name}>
            {device.displayName}
          </Item>
        ))}
      </Picker>
      {/* <Button
          variant="cta"
          UNSAFE_style={{
            height: "28px",
            fontSize: "12px",
            padding: "0 12px",
          }}
          onPress={testRingerHandler}
        >
          Test Ringer
        </Button> */}
      </Flex>
      <Flex direction="row" gap="size-100" alignItems="center" marginTop="size-100" marginBottom="size-200">
        <Text
          UNSAFE_style={{
            fontSize: "12px",
            color: "#6E6E6E",
            fontStyle: "italic",
          }}
        >
          Audio device for ringtones. Once you answer, call audio will play through the selected Speaker device.
        </Text>
      </Flex>
    </Flex>
  );
};

export default AudioSettings;
